import React from "react";
import { Routes, Route } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { OrigoAuthenticator } from "@oriola-origo/origo-app-shell";
import { rest as RestService } from "@oriola-origo/origo-common-client-lib";
import moment from "moment";
import { StylesProvider } from "@material-ui/core";
import {
  Layout,
  Projects,
  Project,
  CreateProject,
  NotFound,
  ShowOnlyInDevelopment,
  UserInfo,
  ProtectedRoute,
} from "./components";
import {
  userSignIn,
  fetchUserDataFromOrigo,
  userSignOut,
  setTokenData,
} from "./redux/reducers";
import "./App.css";
import { Permissions } from "./utils/auth/permissions";

function App() {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const user = useSelector(state => state.user);
  const { userData } = user;

  // initialize axios iintercepter
  if (user.tokenData && user.sessionId) {
    RestService.init(user, res => dispatch(setTokenData(res)));
  }

  const initialize = signInData => {
    // set signed in
    dispatch(userSignIn(signInData));

    // update user data from origo
    const { userId } = signInData.userData;
    dispatch(fetchUserDataFromOrigo(userId))
      .then(data => {
        if (data && data.language) {
          i18n.changeLanguage(data.language);
          moment.locale(data.language);
        }
      })
      .catch(error => {
        // Handle error gracefully - user is still signed in
        // eslint-disable-next-line no-console
        console.error("Failed to fetch user data:", error);
      });
  };

  const renderApp = () => (
    <Layout>
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute
              failComponent={<NotFound />}
              userData={userData}
              permission={Permissions.VIEW_PROJECTS}
            >
              <Projects />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId"
          element={
            <ProtectedRoute
              failComponent={<NotFound />}
              userData={userData}
              permission={Permissions.VIEW_PROJECTS}
            >
              <Project />
            </ProtectedRoute>
          }
        />
        <Route
          path="/create"
          element={
            <ProtectedRoute
              failComponent={<NotFound />}
              userData={userData}
              permission={Permissions.CREATE_PROJECT}
            >
              <CreateProject />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user"
          element={
            <ShowOnlyInDevelopment isRoute>
              <UserInfo />
            </ShowOnlyInDevelopment>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );

  return (
    <OrigoAuthenticator
      isSignedIn={user.signedIn}
      onSignedIn={signInData => initialize(signInData)}
      onSignedOut={() => {
        // clear
        dispatch(userSignOut());
      }}
    >
      <StylesProvider injectFirst>{renderApp()}</StylesProvider>
    </OrigoAuthenticator>
  );
}

export default App;
